import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";

const TopPicks = ({ houses, onLocationClick }) => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Category icon mapping (same as App.js)
  const getCategoryIcon = (category) => {
    const categoryIconMap = {
      house: "/markers/house.png",
      community: "/markers/community.png",
      event: "/markers/events.png",
      events: "/markers/events.png",
      market: "/markers/market.png",
      charity: "/markers/charity.png",
      santa: "/markers/santa.png",
      parade: "/markers/parade.png",
      show: "/markers/shows.png",
      shows: "/markers/shows.png",
      skating: "/markers/skating.png",
      restaurant: "/markers/dining.png",
      dining: "/markers/dining.png",
      tree_farm: "/markers/christmastree_or_all.png",
      shopping: "/markers/market.png",
      all: "/markers/christmastree_or_all.png",
    };

    const normalizedCategory = category?.toLowerCase() || "all";
    return categoryIconMap[normalizedCategory] || categoryIconMap.all;
  };

  // Filter and sort locations
  const filteredAndSorted = useMemo(() => {
    let filtered = [...houses];

    // Filter by category
    if (selectedTab !== "all") {
      filtered = filtered.filter(
        (loc) => (loc.category || loc.type || "all")?.toLowerCase() === selectedTab
      );
    }

    // Filter by search query
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((loc) => {
        const nameMatch = loc.name?.toLowerCase().includes(query);
        const addressMatch = loc.address?.toLowerCase().includes(query);
        return nameMatch || addressMatch;
      });
    }

    // Sort by votes (descending)
    const sorted = filtered.sort((a, b) => (b.votes || 0) - (a.votes || 0));
    
    // Limit to top 10 locations only (to prevent copying all locations)
    return sorted.slice(0, 10);
  }, [houses, selectedTab, searchQuery]);

  const top3 = filteredAndSorted.slice(0, 3);
  const rest = filteredAndSorted.slice(3);

  const categories = [
    { id: "all", label: "All" },
    { id: "house", label: "Houses" },
    { id: "event", label: "Events" },
    { id: "community", label: "Community" },
    { id: "market", label: "Markets" },
    { id: "santa", label: "Santa" },
    { id: "tree_farm", label: "Trees" },
    { id: "show", label: "Shows" },
    { id: "skating", label: "Skating" },
    { id: "restaurant", label: "Dining" },
    { id: "shopping", label: "Shopping" },
    { id: "parade", label: "Parades" },
    { id: "charity", label: "Charity" },
  ];

  const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  const medalEmojis = ["🥇", "🥈", "🥉"];
  const rankLabels = ["1st", "2nd", "3rd"];

  const handleLocationClick = (location) => {
    // Open the info modal if handler is provided
    if (onLocationClick) {
      onLocationClick(location);
    } else {
      // Fallback: Navigate to map and center on this location
      if (location.location || (location.latitude && location.longitude)) {
        const lat = location.location?.lat || location.latitude;
        const lng = location.location?.lng || location.longitude;
        navigate(`/map?lat=${lat}&lng=${lng}`);
      } else {
        navigate("/map");
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#FAFAFA",
        paddingTop: { xs: "80px", sm: "100px" },
        paddingBottom: "40px",
      }}
    >
      <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: { xs: "16px", sm: "24px" } }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", marginBottom: "32px" }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 800,
              color: "#C41E3A",
              marginBottom: "8px",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            🏆 Top Picks
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Playfair Display', serif",
              color: "#666",
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            Top 10 most voted Christmas light displays
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{ marginBottom: "24px" }}>
          <TextField
            fullWidth
            placeholder="Search locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#C41E3A" }} />
                </InputAdornment>
              ),
              endAdornment:
                searchQuery.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery("")}
                      sx={{ color: "#666" }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#C41E3A",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#C41E3A",
                },
              },
            }}
          />
        </Box>

        {/* Category Tabs */}
        <Box
          sx={{
            marginBottom: "32px",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
          }}
        >
          <Box sx={{ display: "flex", gap: "8px", minWidth: "fit-content" }}>
            {categories.map((cat) => {
              const isSelected = selectedTab === cat.id;
              return (
                <Chip
                  key={cat.id}
                  label={cat.label}
                  onClick={() => setSelectedTab(cat.id)}
                  sx={{
                    backgroundColor: isSelected ? "#C41E3A" : "#F5F5F5",
                    color: isSelected ? "#FFFFFF" : "#666",
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: isSelected ? 600 : 500,
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                    overflow: "visible",
                    maxWidth: "none",
                    "& .MuiChip-label": {
                      overflow: "visible",
                      whiteSpace: "nowrap",
                      textOverflow: "clip",
                      padding: "0 8px",
                    },
                    "&:hover": {
                      backgroundColor: isSelected ? "#DC143C" : "#E0E0E0",
                    },
                    ...(isSelected && {
                      boxShadow: "0 2px 8px rgba(196, 30, 58, 0.3)",
                    }),
                  }}
                  icon={
                    cat.id !== "all" ? (
                      <Box
                        component="img"
                        src={getCategoryIcon(cat.id)}
                        alt={cat.label}
                        sx={{
                          width: "20px",
                          height: "20px",
                          opacity: isSelected ? 1 : 0.6,
                          marginLeft: "4px",
                        }}
                      />
                    ) : null
                  }
                />
              );
            })}
          </Box>
        </Box>

        {/* Results Count */}
        {(searchQuery.trim().length > 0 || selectedTab !== "all") && (
          <Box sx={{ marginBottom: "24px" }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Playfair Display', serif",
                color: "#666",
                fontSize: "0.875rem",
              }}
            >
              {searchQuery.trim().length > 0 && selectedTab !== "all"
                ? `Showing ${filteredAndSorted.length} ${selectedTab} result${filteredAndSorted.length !== 1 ? "s" : ""} for "${searchQuery}"`
                : searchQuery.trim().length > 0
                ? `Showing ${filteredAndSorted.length} result${filteredAndSorted.length !== 1 ? "s" : ""} for "${searchQuery}"`
                : `Showing ${filteredAndSorted.length} ${selectedTab} location${filteredAndSorted.length !== 1 ? "s" : ""}`}
            </Typography>
          </Box>
        )}

        {/* Top 3 Section */}
        {top3.length >= 3 && (
          <Box sx={{ marginBottom: "40px" }}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                color: "#333",
                marginBottom: "16px",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              Top 3
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              {top3.map((location, index) => (
                <Card
                  key={location.id}
                  onClick={() => handleLocationClick(location)}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: index === 0 ? "2px solid #C41E3A" : "1px solid #E0E0E0",
                    borderRadius: "16px",
                    boxShadow:
                      index === 0
                        ? "0 4px 20px rgba(196, 30, 58, 0.2)"
                        : "0 2px 8px rgba(0, 0, 0, 0.1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <CardContent sx={{ padding: "20px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: "44px",
                          height: "44px",
                          backgroundColor: medalColors[index],
                          fontSize: "20px",
                        }}
                      >
                        {medalEmojis[index]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: "#333",
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                            marginBottom: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {location.name || location.address || "Unknown Location"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            color: "#999",
                            fontSize: "0.75rem",
                          }}
                        >
                          {rankLabels[index]} Place
                        </Typography>
                      </Box>
                    </Box>

                    {location.address && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "12px",
                        }}
                      >
                        <LocationOnIcon sx={{ fontSize: "16px", color: "#999" }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            color: "#666",
                            fontSize: "0.8rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {location.address}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: "12px",
                        paddingTop: "12px",
                        borderTop: "1px solid #F0F0F0",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Box
                          component="img"
                          src={getCategoryIcon(location.category || location.type)}
                          alt={location.category || "all"}
                          sx={{ width: "24px", height: "24px" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            color: "#999",
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {location.category || location.type || "All"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          backgroundColor: "#F5F5F5",
                          padding: "4px 12px",
                          borderRadius: "12px",
                        }}
                      >
                        <ThumbUpIcon sx={{ fontSize: "16px", color: "#C41E3A" }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: "#C41E3A",
                            fontSize: "0.875rem",
                          }}
                        >
                          {location.votes || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Rest of the List */}
        {rest.length > 0 && (
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                color: "#333",
                marginBottom: "16px",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              }}
            >
              More Great Picks
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                gap: "16px",
              }}
            >
              {rest.map((location, index) => {
                const actualRank = index + 4;
                return (
                  <Card
                    key={location.id}
                    onClick={() => handleLocationClick(location)}
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      border: "1px solid #E0E0E0",
                      borderRadius: "12px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ padding: "16px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#F5F5F5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: "#666",
                            fontSize: "0.875rem",
                          }}
                        >
                          {actualRank}
                        </Box>
                        <Box
                          component="img"
                          src={getCategoryIcon(location.category || location.type)}
                          alt={location.category || "all"}
                          sx={{ width: "24px", height: "24px" }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontFamily: "'Playfair Display', serif",
                              fontWeight: 600,
                              color: "#333",
                              fontSize: "0.9rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {location.name || location.address || "Unknown Location"}
                          </Typography>
                          {location.address && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "'Playfair Display', serif",
                                color: "#999",
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                              }}
                            >
                              {location.address}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                          marginTop: "8px",
                        }}
                      >
                        <ThumbUpIcon sx={{ fontSize: "16px", color: "#C41E3A" }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: "#C41E3A",
                            fontSize: "0.875rem",
                          }}
                        >
                          {location.votes || 0} votes
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {filteredAndSorted.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              padding: "60px 20px",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: "'Playfair Display', serif",
                marginBottom: "16px",
                fontSize: "3rem",
              }}
            >
              🎄
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 600,
                color: "#333",
                marginBottom: "8px",
              }}
            >
              No locations yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Playfair Display', serif",
                color: "#666",
              }}
            >
              Be the first to add a {selectedTab === "all" ? "location" : selectedTab}!
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TopPicks;

